import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SocialFooter from '../components/SocialFooter';
import { ArrowLeft, MagnifyingGlass, Question, CaretDown, CaretUp, Lifebuoy, CreditCard, Gear, ShieldCheck } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const FAQ_CATEGORIES = [
  {
    category: 'Getting Started',
    icon: Lifebuoy,
    items: [
      { q: 'How do I log a workout?', a: 'From your Dashboard, tap any machine button (Treadmill, Elliptical, etc.) to open the tracker. Enter your start time, end time, distance, and calories. The total duration is calculated automatically.' },
      { q: 'What machines can I track?', a: 'Track365 supports 8 machines: Treadmill, Elliptical, Stair Climber, Row Machine, Running, Speed Walking, Stationary Bike, and Weekly Weight logging.' },
      { q: 'How do streaks work?', a: 'Your streak counts consecutive days where you logged at least one workout. If you miss a day, your streak resets. Multiple workouts on the same day count as one day.' },
      { q: 'What is the 365-day journey?', a: 'Starting from your join date, Track365 counts 365 days. You\'ll see how many days you\'ve completed, missed, and how many remain. The goal is to build a full year of fitness habits.' },
    ],
  },
  {
    category: 'Billing & Subscription',
    icon: CreditCard,
    items: [
      { q: 'What plans are available?', a: 'We offer a Free plan (basic tracking, 7-day history) and Premium plans: Monthly at $7.99/mo and Annual at $49.99/yr (best value at just $4.17/mo).' },
      { q: 'How do I cancel my subscription?', a: 'Go to Settings > Subscription to manage or cancel your plan. Cancellation stops future billing, but you keep access through the end of your current period.' },
      { q: 'Can I get a refund?', a: 'All purchases are final per our Refund Policy. If you have concerns, contact support@track365cardio.com before initiating any disputes.' },
    ],
  },
  {
    category: 'Account & Settings',
    icon: Gear,
    items: [
      { q: 'How do I change my password?', a: 'Go to Profile > Settings to update your password. If you signed up with Google, your password is managed through your Google account.' },
      { q: 'How do I delete my account?', a: 'Go to Settings > Delete Account. You\'ll need to type DELETE to confirm. This action is permanent and removes all your data.' },
      { q: 'Can I export my data?', a: 'We\'re working on data export functionality. In the meantime, contact support@track365cardio.com for a manual export request.' },
    ],
  },
  {
    category: 'Privacy & Data',
    icon: ShieldCheck,
    items: [
      { q: 'What data does Track365 collect?', a: 'Only what you log: workout data (duration, calories, distance), weight entries, and basic profile info. We never access your location or contacts.' },
      { q: 'Is my data shared with third parties?', a: 'No. We never sell or share your personal data with third parties. See our Privacy Policy for full details.' },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="text-sm font-medium text-foreground pr-4">{q}</span>
        {open ? (
          <CaretUp weight="bold" className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <CaretDown weight="bold" className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="pb-4 -mt-1">
          <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

const HelpCenter = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filteredCategories = FAQ_CATEGORIES.map(cat => ({
    ...cat,
    items: cat.items.filter(item =>
      item.q.toLowerCase().includes(search.toLowerCase()) ||
      item.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="help-center-screen">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <img src={LOGO_URL} alt="Track365" className="w-40 h-auto" />
          <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Help Center</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 relative z-10">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-foreground mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground mb-4">Find answers to common questions about Track365.</p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <MagnifyingGlass weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search help articles..."
              className="w-full bg-card border border-border rounded-xl pl-10 pr-4 py-3 text-foreground text-sm focus:outline-none focus:border-[#D4AF37]"
              data-testid="help-search"
            />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-6 mb-8">
          {filteredCategories.map((cat, idx) => (
            <div key={idx} className="premium-card p-5" data-testid={`faq-category-${idx}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <cat.icon weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="text-foreground font-semibold">{cat.category}</h3>
              </div>
              <div className="divide-y divide-border">
                {cat.items.map((item, i) => (
                  <FAQItem key={i} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <Question weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No results found. Try a different search term.</p>
          </div>
        )}

        {/* Still need help? */}
        <div className="premium-card p-5 text-center mb-6">
          <p className="text-foreground font-semibold mb-2">Still need help?</p>
          <p className="text-sm text-muted-foreground mb-3">Our support team is here for you.</p>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-bold px-6 py-3 rounded-full hover:bg-[#F3E5AB] transition-all text-sm"
            data-testid="contact-support-btn"
          >
            Contact Support
          </Link>
        </div>

        <SocialFooter />
      </main>
    </div>
  );
};

export default HelpCenter;
